/**
 * Utilidades de presentación de Turnos en vivo.
 *
 * Las fechas del backend llegan como `YYYY-MM-DD HH:mm:ss` (hora local de la
 * estación, sin zona). Se parsean reemplazando el espacio por `T` para que el
 * navegador las interprete como hora local y no como UTC.
 */

const FORMATO_BACKEND = /^(\d{4})-(\d{2})-(\d{2})[ T](\d{2}):(\d{2}):(\d{2})/;

/** Convierte `YYYY-MM-DD HH:mm:ss` a `Date` en hora local. */
export function parseFechaLocal(valor: string | null | undefined): Date | null {
  if (!valor) return null;

  const coincidencia = FORMATO_BACKEND.exec(valor);
  if (!coincidencia) {
    const alternativa = new Date(valor);
    return Number.isNaN(alternativa.getTime()) ? null : alternativa;
  }

  const [, anio, mes, dia, hora, minuto, segundo] = coincidencia;

  return new Date(
    Number(anio),
    Number(mes) - 1,
    Number(dia),
    Number(hora),
    Number(minuto),
    Number(segundo),
  );
}

/** Hora local en formato `hh:mm` (24 h). */
export function formatHoraTurno(valor: string | null | undefined): string {
  const fecha = parseFechaLocal(valor);
  if (!fecha) return '—';

  return fecha.toLocaleTimeString('es-PE', {
    hour: '2-digit',
    minute: '2-digit',
    hour12: false,
  });
}

/** Fecha + hora del turno (`08/04/2026 06:01`). */
export function formatFechaHoraTurno(valor: string | null | undefined): string {
  const fecha = parseFechaLocal(valor);
  if (!fecha) return '—';

  const dia = fecha.toLocaleDateString('es-PE', { day: '2-digit', month: '2-digit', year: 'numeric' });
  const hora = fecha.toLocaleTimeString('es-PE', { hour: '2-digit', minute: '2-digit', hour12: false });

  return `${dia} ${hora}`;
}

/** Tiempo transcurrido legible: `6 d 9 h`, `4 h 12 m`, `18 m`. */
export function formatTiempoTranscurrido(minutos: number): string {
  if (!Number.isFinite(minutos) || minutos < 0) return '—';
  if (minutos < 60) return `${Math.floor(minutos)} m`;

  const horas = Math.floor(minutos / 60);
  const restoMinutos = Math.floor(minutos % 60);

  if (horas < 24) return `${horas} h ${restoMinutos} m`;

  const dias = Math.floor(horas / 24);
  const restoHoras = horas % 24;

  return `${dias} d ${restoHoras} h`;
}

/** Color semántico según cuánto lleva abierto el turno. */
export function tonoAntiguedad(turno: { vencido: boolean; antiguedad_dias: number }): 'ok' | 'warn' | 'danger' {
  if (turno.antiguedad_dias >= 7) return 'danger';
  if (turno.vencido) return 'warn';

  return 'ok';
}
